import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  alpha,
  useTheme,
  Chip,
  CardActionArea,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination
} from "@mui/material";
import { Add, Edit, Delete, PlayArrow, OpenInNew, Refresh, FilterList } from "@mui/icons-material";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";

const defaultCourse = {
  id: null,
  crop: "",
  course_type: "",
  title: "",
  description: "",
  youtube_url: "",
  clicks: 0,
  created_at: null,
  updated_at: null
};

const sanitizeUrl = (url) => {
  try {
    const u = new URL(url);
    return u.toString();
  } catch {
    return "";
  }
};

// Extract YouTube video ID from multiple URL formats
const getYouTubeId = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const pathParts = u.pathname.split("/").filter(Boolean);
    const watchIndex = pathParts.findIndex(p => p === "embed" || p === "v");
    if (watchIndex >= 0 && pathParts[watchIndex + 1]) return pathParts[watchIndex + 1];
  } catch {}
  return "";
};

const getYouTubeThumbnail = (youtubeUrl) => {
  const id = getYouTubeId(youtubeUrl);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
};

const CoursesPage = () => {
  const theme = useTheme();
  const toast = useToast();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  const [courses, setCourses] = useState([]);
  const [crops, setCrops] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultCourse);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCrops, setLoadingCrops] = useState(false);
  const [selectedCrop, setSelectedCrop] = useState('all');
  const [courseTypes, setCourseTypes] = useState([]);
  const [loadingCourseTypes, setLoadingCourseTypes] = useState(false);
  const [selectedCourseType, setSelectedCourseType] = useState(null);
  const [courseTypeId, setCourseTypeId] = useState(null);
  const [courseTypeModalOpen, setCourseTypeModalOpen] = useState(false);
  const [courseTypeForm, setCourseTypeForm] = useState({ id: null, name: '', description: '' });
  const [courseTypeErrors, setCourseTypeErrors] = useState({});
  const [editingCourseType, setEditingCourseType] = useState(null);
  const [categoriesManagementOpen, setCategoriesManagementOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
 const { setPageTitle } = usePageTitle();
  const userId = localStorage.getItem("user_id");
    useEffect(() => {
        setPageTitle('Knowledge Hub ');
    }, [setPageTitle]);
  // Fetch crops from API
  const fetchCrops = async () => {
    setLoadingCrops(true);
    try {
      const response = await apiService.get(`${API_ENDPOINTS.GET_crops}?user_id=${userId}`);
      if (response?.success) {
        setCrops(response.data);
      }
    } catch (error) {
      console.error("Error fetching crops:", error);
      toast.showError("Failed to load crops");
    } finally {
      setLoadingCrops(false);
    }
  };

  // Fetch course types from API
  const fetchCourseTypes = async () => {
    setLoadingCourseTypes(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_course_type);
      if (response?.success && response?.data?.CourseType) {
        const courseTypeList = response.data.CourseType.map(type => ({
          id: type.id,
          name: type.name,
          sequence: type.sequence,
          description: type.description || ''
        }));
        setCourseTypes(courseTypeList);
      }
    } catch (error) {
      console.error("Error fetching course types:", error);
      toast.showError("Failed to load course types");
    } finally {
      setLoadingCourseTypes(false);
    }
  };

  // Open course type modal for create
  const openAddCourseType = () => {
    setEditingCourseType(null);
    setCourseTypeForm({ id: null, name: '', description: '' });
    setCourseTypeErrors({});
    setCourseTypeModalOpen(true);
  };

  // Open course type modal for edit
  const openEditCourseType = (courseType) => {
    setEditingCourseType(courseType.id);
    setCourseTypeForm({ 
      id: courseType.id, 
      name: courseType.name,
      description: courseType.description || ''
    });
    setCourseTypeErrors({});
    setCourseTypeModalOpen(true);
  };

  // Create or update course type
  const handleSaveCourseType = async () => {
    // Validate
    const e = {};
    if (!courseTypeForm.name.trim()) {
      e.name = "Course type name is required";
    }
    setCourseTypeErrors(e);
    if (Object.keys(e).length > 0) return;

    try {
      let response;
      if (editingCourseType) {
        // Update existing course type - API expects course_type_id, name, and description
        response = await apiService.put(API_ENDPOINTS.PUT_update_course_type, {
          course_type_id: editingCourseType,
          name: courseTypeForm.name.trim(),
          description: courseTypeForm.description?.trim() || ''
        });
      } else {
        // Create new course type
        response = await apiService.post(API_ENDPOINTS.POST_create_course_type, {
          name: courseTypeForm.name.trim()
        });
      }

      if (response?.success) {
        const message = editingCourseType ? "Course category updated successfully!" : "Course category added successfully!";
        toast.showSuccess(message);
        setCourseTypeModalOpen(false);
        const wasEditing = editingCourseType;
        const savedName = courseTypeForm.name.trim();
        setCourseTypeForm({ id: null, name: '', description: '' });
        setCourseTypeErrors({});
        setEditingCourseType(null);
        // Refresh course types list
        await fetchCourseTypes();
        // If the edited category was selected, update the selection
        if (wasEditing && selectedCourseType?.id === wasEditing) {
          setSelectedCourseType({ ...selectedCourseType, name: savedName });
        }
        // Return true to indicate success
        return true;
      } else {
        const errorMessage = response?.message || (editingCourseType ? "Failed to update course category" : "Failed to add course category");
        toast.showError(errorMessage);
        return false;
      }
    } catch (error) {
      console.error("Error saving course type:", error);
      const errorMessage = editingCourseType ? "Failed to update course category" : "Failed to add course category";
      toast.showError(errorMessage);
      return false;
    }
  };

  // Delete course type
  const handleDeleteCourseType = async (courseTypeId, courseTypeName) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Category",
      message: `Are you sure you want to delete the category "${courseTypeName}"? This action cannot be undone.`,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await apiService.delete(`${API_ENDPOINTS.DELETE_course_type}?course_type_id=${courseTypeId}`);

      if (response?.success) {
        const message = "Course category deleted successfully!";
        toast.showSuccess(message);
        // If the deleted category was selected, clear the selection
        if (selectedCourseType?.id === courseTypeId) {
          setSelectedCourseType(null);
          setCourseTypeId(null);
          fetchCourses(selectedCrop, null, 1, pageSize);
        }
        // Refresh course types list
        fetchCourseTypes();
      } else {
        const errorMessage = response?.message || "Failed to delete course category";
        toast.showError(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting course type:", error);
      const errorMessage = "Failed to delete course category";
      toast.showError(errorMessage);
    }
  };



  // Filter by course type (uses fetchCourses with course_type_id)
  const filterCoursesByType = async (typeId, page = 1, pageSizeParam = 10) => {
    await fetchCourses(selectedCrop, typeId, page, pageSizeParam);
  };

  // Fetch courses from API with pagination
  const fetchCourses = async (cropId = 'all', courseTypeIdParam = null, page = 1, pageSizeParam = 10) => {
    setLoading(true);
    try {
      let endpoint = API_ENDPOINTS.GET_courses;
      const params = new URLSearchParams();
      params.set('user_id', userId || '');
      params.set('page', String(page));
      params.set('page_size', String(pageSizeParam));
      if (cropId !== 'all') params.set('crop_id', cropId);
      if (courseTypeIdParam) params.set('course_type_id', String(courseTypeIdParam));
      endpoint += (endpoint.includes('?') ? '&' : '?') + params.toString();

      const response = await apiService.get(endpoint);
      if (response?.success) {
        setCourses(Array.isArray(response.data) ? response.data : []);
        const paginationData = response?.pagination || response?.data?.pagination || {};
        if (Object.keys(paginationData).length > 0) {
          setPagination({
            current_page: paginationData.current_page ?? page,
            page_size: paginationData.page_size ?? pageSizeParam,
            total_items: paginationData.total_items ?? 0,
            total_pages: paginationData.total_pages ?? 1,
            has_next: paginationData.has_next ?? false,
            has_previous: paginationData.has_previous ?? false,
            next_page_number: paginationData.next_page_number ?? null,
            previous_page_number: paginationData.previous_page_number ?? null,
          });
          setCurrentPage(paginationData.current_page ?? page);
          setPageSize(paginationData.page_size ?? pageSizeParam);
        } else {
          const list = Array.isArray(response.data) ? response.data : [];
          setPagination(prev => ({ ...prev, total_items: list.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
        }
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.showError("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    if (courseTypeId) {
      filterCoursesByType(courseTypeId, value, pageSize);
    } else {
      fetchCourses(selectedCrop, null, value, pageSize);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    if (courseTypeId) {
      filterCoursesByType(courseTypeId, 1, newSize);
    } else {
      fetchCourses(selectedCrop, null, 1, newSize);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCrops();
    fetchCourses('all', null, 1, 10);
    fetchCourseTypes();
  }, []);

  const resetForm = () => {
    setForm(defaultCourse);
    setErrors({});
    setEditing(null);
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Title is required";
    if (!form.description.trim()) e.description = "Description is required";
    if (!form.youtube_url.trim()) e.youtube_url = "YouTube link is required";
    if (form.youtube_url && !sanitizeUrl(form.youtube_url)) e.youtube_url = "Enter a valid URL";
    if (!form.crop) e.crop = "Please select a crop";
    if (!form.course_type) e.course_type = "Please select a course type";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openAdd = () => {
    resetForm();
    // Pre-fill course_type if a course type tab is selected
    if (courseTypeId) {
      setForm({ ...defaultCourse, course_type: courseTypeId });
    }
    setModalOpen(true);
  };

  const openEdit = (course) => {
    setEditing(course.id);
    // Extract course_type_id - check multiple possible property names and handle type conversion
    let courseTypeId = "";
    
    // Try course_type_id first (most common)
    if (course.course_type_id !== undefined && course.course_type_id !== null && course.course_type_id !== "") {
      courseTypeId = course.course_type_id;
    } 
    // Then try course_type (could be ID or object)
    else if (course.course_type !== undefined && course.course_type !== null && course.course_type !== "") {
      // If course_type is an object with id property
      if (typeof course.course_type === 'object' && course.course_type !== null) {
        if (course.course_type.id !== undefined) {
          courseTypeId = course.course_type.id;
        }
      } else {
        // course_type is a primitive value (number or string)
        courseTypeId = course.course_type;
      }
    }
    
    // Debug: log the course object to see its structure (can be removed later)
    console.log("Edit course object:", course);
    console.log("Extracted courseTypeId:", courseTypeId);
    
    setForm({ 
      id: course.id,
      crop: course.crop,
      course_type: courseTypeId,
      title: course.title,
      description: course.description,
      youtube_url: course.youtube_url,
      clicks: course.clicks,
      created_at: course.created_at,
      updated_at: course.updated_at
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Course",
      message: "Are you sure you want to delete this course?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    try {
      const response = await apiService.delete(`${API_ENDPOINTS.DELETE_course}?course_id=${id}`);
      if (response?.success) {
        const message = "Course deleted successfully!";
        toast.showSuccess(message);
        if (courseTypeId) filterCoursesByType(courseTypeId, currentPage, pageSize);
        else fetchCourses(selectedCrop, null, currentPage, pageSize);
      } else {
        const errorMessage = response?.message || "Failed to delete course";
        toast.showError(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting course:", error);
      const errorMessage = "Failed to delete course";
      toast.showError(errorMessage);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    try {
      const courseData = {
        crop: form.crop,
        course_type_id: form.course_type,
        title: form.title,
        description: form.description,
        youtube_url: form.youtube_url
      };

      let response;
      if (editing) {
        courseData.course_id = editing;
        response = await apiService.put(`${API_ENDPOINTS.PUT_update_course}`, courseData);
      } else {
        response = await apiService.post(API_ENDPOINTS.POST_create_course, courseData);
      }

      if (response?.success) {
        const message = editing ? "Course updated successfully!" : "Course added successfully!";
        toast.showSuccess(message);
        setModalOpen(false);
        resetForm();
        // Refresh courses based on current filters
        if (courseTypeId) {
          filterCoursesByType(courseTypeId, currentPage, pageSize);
        } else {
          fetchCourses(selectedCrop, null, currentPage, pageSize);
        }
      }
    } catch (error) {
      console.error("Error saving course:", error);
      const errorMessage = "Failed to save course";
      toast.showError(errorMessage);
    }
  };

  const handleOpenAndCount = (course) => {
    const safeUrl = sanitizeUrl(course.youtube_url);
    if (!safeUrl) return;
    window.open(safeUrl, '_blank', 'noopener');
  };

  const getCardThumbnail = (course) => {
    return getYouTubeThumbnail(course.youtube_url) || "/static/thumbnail/thumbnail-1.png";
  };

  const getCropName = (cropId) => {
    const crop = crops.find(c => c.id === cropId);
    return crop ? crop.name : 'Unknown Crop';
  };



 const handleCropFilterChange = (cropId) => {
  setSelectedCrop(cropId);
  setCurrentPage(1);
  if (courseTypeId) {
    fetchCourses(cropId, courseTypeId, 1, pageSize);
  } else {
    fetchCourses(cropId, null, 1, pageSize);
  }
};

  const handleCourseTypeChange = (courseType) => {
    if (courseType) {
      setSelectedCourseType(courseType);
      setCourseTypeId(courseType.id);
      setCurrentPage(1);
      filterCoursesByType(courseType.id, 1, pageSize);
    } else {
      setSelectedCourseType(null);
      setCourseTypeId(null);
      setCurrentPage(1);
      fetchCourses(selectedCrop, null, 1, pageSize);
    }
  };

  return (
    <Box p={2}>
      <Card sx={{ p: 2, mb: 2, background: alpha(theme.palette.primary.main, 0.02), border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Typography variant="h6" fontWeight={600}>Courses</Typography>
           
          
          <Box display="flex" alignItems="center" gap={2}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 200 }}
              options={[{ id: 'all', name: 'All Crops' }, ...crops]}
              getOptionLabel={(option) => option.name}
              value={selectedCrop === 'all' ? { id: 'all', name: 'All Crops' } : crops.find(c => c.id === selectedCrop) || null}
              onChange={(event, newValue) => {
                const cropId = newValue ? newValue.id : 'all';
                handleCropFilterChange(cropId);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Crop"
                  placeholder="Search crops..."
                />
              )}
              loading={loadingCrops}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                return options.filter(option => 
                  option.name.toLowerCase().includes(inputValue.toLowerCase())
                );
              }}
            />
            {/* <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={fetchCourseTypes}
              disabled={loadingCourseTypes}
              sx={{ textTransform: 'none' }}
            >
              Filter Category
            </Button> */}
            <Box display="flex" alignItems="center" gap={1}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 200 }}
                options={courseTypes}
                getOptionLabel={(option) => option.name}
                value={selectedCourseType}
                onChange={(event, newValue) => {
                  handleCourseTypeChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter Category"
                    placeholder={courseTypes.length === 0 ? "No categories available" : "Select category..."}
                  />
                )}
                loading={loadingCourseTypes}
                disabled={courseTypes.length === 0 && !loadingCourseTypes}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  return options.filter(option => 
                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={() => setCategoriesManagementOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Manage Categories
              </Button>
            </Box>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />} 
              onClick={() => {
                if (courseTypeId) {
                  filterCoursesByType(courseTypeId, currentPage, pageSize);
                } else {
                  fetchCourses(selectedCrop, null, currentPage, pageSize);
                }
              }}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Rows</InputLabel>
              <Select value={pageSize} label="Rows" onChange={handlePageSizeChange}>
                {pageSizeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ textTransform: 'none' }}>
              Add Course
            </Button>
          </Box>
        </Box>
      </Card>

      {loading ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading courses...</Typography>
        </Card>
      ) : courses.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No courses found</Typography>
          <Typography color="text.secondary" gutterBottom>
            {selectedCrop !== 'all' 
              ? `No courses available for ${getCropName(selectedCrop)}`
              : "Add your first course with title, description, and YouTube link."
            }
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ textTransform: 'none', mt: 1 }}>
            Add Course
          </Button>
        </Card>
      ) : (
        <>
        <Grid container spacing={2}>
          {courses.map(course => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
              <Card sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea onClick={() => handleOpenAndCount(course)}>
                  <Box sx={{ position: 'relative', pt: '56.25%', overflow: 'hidden' }}>
                    <Box
                      component="img"
                      src={getCardThumbnail(course)}
                      alt={course.title}
                      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                </CardActionArea>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap title={course.title}>
                      {course.title}
                    </Typography>
                    <Chip label={`Clicks: ${course.clicks_count || course.clicks || 0}`} size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {course.description}
                  </Typography>
                  <Chip 
                    label={course.crop_name || getCropName(course.crop)} 
                    size="small" 
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                    <Button size="small" startIcon={<OpenInNew />} onClick={() => handleOpenAndCount(course)} sx={{ textTransform: 'none' }}>
                      Open
                    </Button>
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => openEdit(course)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(course.id)} size="small">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
          {/* <Typography variant="body2" color="text.secondary">
            {pagination.total_items > 0
              ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items}`
              : 'No courses'}
          </Typography> */}
          
          {pagination.total_pages > 1 && (
            <Pagination count={pagination.total_pages} page={currentPage} onChange={handlePageChange} color="primary" />
          )}
           
        </Box>
        </>
      )}
      <Box
  sx={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    mt: 3,
  }}
>
  <Typography variant="body2" color="text.secondary">
    {pagination.total_items > 0
      ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(
          pagination.current_page * pagination.page_size,
          pagination.total_items
        )} of ${pagination.total_items}`
      : "No courses"}
  </Typography>
</Box>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Course' : 'Add Course'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                error={!!errors.title}
                helperText={errors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={courseTypes}
                getOptionLabel={(option) => option.name}
                value={(() => {
                  if (!form.course_type || form.course_type === '') return null;
                  // Handle type conversion for matching (number vs string)
                  const found = courseTypes.find(ct => {
                    const ctId = typeof ct.id === 'number' ? ct.id : parseInt(ct.id);
                    const formTypeId = typeof form.course_type === 'number' ? form.course_type : parseInt(form.course_type);
                    const matches = ctId === formTypeId && !isNaN(ctId) && !isNaN(formTypeId);
                    return matches;
                  });
                  // Debug: log matching process
                  console.log("Autocomplete - form.course_type:", form.course_type, "courseTypes:", courseTypes, "found:", found);
                  return found || null;
                })()}
                onChange={(event, newValue) => {
                  setForm({ ...form, course_type: newValue ? newValue.id : '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Course Type"
                    placeholder="Search and select course type..."
                    error={!!errors.course_type}
                    helperText={errors.course_type}
                  />
                )}
                loading={loadingCourseTypes}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  return options.filter(option => 
                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={crops}
                getOptionLabel={(option) => option.name}
                value={crops.find(c => c.id === form.crop) || null}
                onChange={(event, newValue) => {
                  setForm({ ...form, crop: newValue ? newValue.id : '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Crop"
                    placeholder="Search and select crop..."
                    error={!!errors.crop}
                    helperText={errors.crop}
                  />
                )}
                loading={loadingCrops}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  return options.filter(option => 
                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="YouTube Link"
                value={form.youtube_url}
                onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                error={!!errors.youtube_url}
                helperText={errors.youtube_url || 'Paste a valid YouTube URL'}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Preview</Typography>
              <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.6)}`, width: '100%', maxWidth: 480 }}>
                <Box component="img" src={getYouTubeThumbnail(form.youtube_url) || "/static/thumbnail/thumbnail-1.png"} alt="Preview" sx={{ width: '100%', display: 'block' }} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Categories Management Modal */}
      <Dialog 
        open={categoriesManagementOpen} 
        onClose={() => {
          setCategoriesManagementOpen(false);
          setCourseTypeModalOpen(false);
          setCourseTypeForm({ id: null, name: '' });
          setCourseTypeErrors({});
          setEditingCourseType(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={600}>
              Manage Course Categories
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openAddCourseType}
              sx={{ textTransform: 'none' }}
            >
              Add Category
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingCourseTypes ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : courseTypes.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No categories found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click "Add Category" to create your first category
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={openAddCourseType}
                sx={{ textTransform: 'none' }}
              >
                Add Category
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sequence</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {courseTypes.map((category, index) => (
                    <TableRow key={category.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {category.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={category.sequence || '-'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Tooltip title="Edit Category">
                            <IconButton
                              size="small"
                              onClick={() => {
                                openEditCourseType(category);
                                setCategoriesManagementOpen(false);
                              }}
                              sx={{ color: 'primary.main' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Category">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteCourseType(category.id, category.name)}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCategoriesManagementOpen(false);
              setCourseTypeModalOpen(false);
              setCourseTypeForm({ id: null, name: '', description: '' });
              setCourseTypeErrors({});
              setEditingCourseType(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Course Type Creation/Edit Modal */}
      <Dialog 
        open={courseTypeModalOpen} 
        onClose={() => {
          setCourseTypeModalOpen(false);
          setCourseTypeForm({ id: null, name: '', description: '' });
          setCourseTypeErrors({});
          setEditingCourseType(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingCourseType ? 'Edit Course Category' : 'Add New Course Category'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                value={courseTypeForm.name}
                onChange={(e) => setCourseTypeForm({ ...courseTypeForm, name: e.target.value })}
                error={!!courseTypeErrors.name}
                helperText={courseTypeErrors.name || 'Enter a name for the course category'}
                placeholder="e.g., Beginner Courses, Advanced Training, etc."
                autoFocus
                required
              />
            </Grid>
            {/* {editingCourseType && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  minRows={3}
                  value={courseTypeForm.description || ''}
                  onChange={(e) => setCourseTypeForm({ ...courseTypeForm, description: e.target.value })}
                  error={!!courseTypeErrors.description}
                  helperText={courseTypeErrors.description || 'Enter a description for the course category (optional)'}
                  placeholder="Enter a description for this category..."
                />
              </Grid>
            )} */}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCourseTypeModalOpen(false);
            setCourseTypeForm({ id: null, name: '', description: '' });
            setCourseTypeErrors({});
            setEditingCourseType(null);
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={async () => {
            const success = await handleSaveCourseType();
            // Reopen management modal after successful save if it was opened from there
            if (success && categoriesManagementOpen) {
              setTimeout(() => {
                setCategoriesManagementOpen(true);
              }, 100);
            }
          }}>
            {editingCourseType ? 'Update Category' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoursesPage; 